import React, { useRef, useEffect } from 'react';
import { ChatMessage as ChatMessageType, Conversation } from '../../types';
import { ChatMessage } from '../atoms';
import { ChatHeader, ChatInput, ConversationHistoryPanel } from '../molecules';

interface ChatWindowProps {
  messages: ChatMessageType[];
  isLoading?: boolean;
  onSendMessage: (message: string) => void;
  onMinimize?: () => void;
  onClose?: () => void;
  title?: string;
  // Historial
  conversations?: Conversation[];
  isHistoryOpen?: boolean;
  onShowHistory?: () => void;
  onCloseHistory?: () => void;
  onSelectConversation?: (conversationId: string) => void;
  onNewConversation?: () => void;
  onRenameConversation?: (conversationId: string, newName: string) => void;
  onDeleteConversation?: (conversationId: string) => void;
  selectedConversationId?: string | null;
}

const ChatWindow: React.FC<ChatWindowProps> = ({
  messages,
  isLoading = false,
  onSendMessage,
  onMinimize,
  onClose,
  title = 'Asistente Virtual',
  conversations = [],
  isHistoryOpen = false,
  onShowHistory,
  onCloseHistory,
  onSelectConversation,
  onNewConversation,
  onRenameConversation,
  onDeleteConversation,
  selectedConversationId,
}) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  return (
    <>
      <div className="flex flex-col h-full bg-white rounded-lg shadow-lg overflow-hidden">
        <ChatHeader
          title={title}
          onMinimize={onMinimize}
          onClose={onClose}
          onShowHistory={onShowHistory}
        />
        <div className="flex-1 overflow-y-auto p-4 bg-gray-50">
          {messages.length === 0 ? (
            <div className="flex items-center justify-center h-full text-gray-500">
              <div className="text-center">
                <p className="text-sm">¡Hola! ¿En qué puedo ayudarte?</p>
              </div>
            </div>
          ) : (
            <>
              {messages.map((message) => (
                <ChatMessage key={message.id} message={message} />
              ))}
              {isLoading && (
                <div className="flex justify-start mb-4">
                  <div className="bg-gray-100 rounded-lg px-4 py-2">
                    <p className="text-sm text-gray-500">Escribiendo...</p>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </>
          )}
        </div>
        <ChatInput onSendMessage={onSendMessage} isLoading={isLoading} />
      </div>

      {/* Panel de Historial */}
      {onShowHistory && onCloseHistory && (
        <ConversationHistoryPanel
          isOpen={isHistoryOpen}
          onClose={onCloseHistory}
          conversations={conversations}
          selectedConversationId={selectedConversationId || null}
          onSelectConversation={onSelectConversation || (() => {})}
          onNewConversation={onNewConversation || (() => {})}
          onRenameConversation={onRenameConversation || (() => {})}
          onDeleteConversation={onDeleteConversation}
          isLoading={isLoading}
        />
      )}
    </>
  );
};

export default ChatWindow;

